import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Package, GraduationCap, Award, Heart, MessageSquare, User as UserIcon,
  LogOut, Loader2, Truck, Plus, Send, ShoppingBag, Star,
} from "lucide-react";
import { toast } from "sonner";
import { money } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "orders", label: "Orders", icon: Package },
  { key: "courses", label: "My Learning", icon: GraduationCap },
  { key: "certificates", label: "Certificates", icon: Award },
  { key: "wishlist", label: "Wishlist", icon: Heart },
  { key: "tickets", label: "Support", icon: MessageSquare },
  { key: "profile", label: "Profile", icon: UserIcon },
];

const Empty = ({ icon: Icon, text, cta, to }) => (
  <div className="text-center py-16 border border-dashed border-black/15 bg-white" data-testid="account-empty">
    <Icon className="w-10 h-10 text-black/20 mx-auto" />
    <p className="text-sm text-muted-foreground mt-3">{text}</p>
    {cta && <Link to={to} className="inline-block mt-3 text-xs font-bold uppercase tracking-wider text-taven-gold hover:underline">{cta}</Link>}
  </div>
);

export default function Account() {
  const { user, loading, logout, authApi, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [orders, setOrders] = useState(null);
  const [certs, setCerts] = useState(null);
  const [progress, setProgress] = useState(null);
  const [wishlist, setWishlist] = useState(null);
  const [tickets, setTickets] = useState(null);

  useEffect(() => { if (!loading && !user) navigate("/login", { state: { from: "/account" } }); }, [loading, user, navigate]);

  const fetchOrders = useCallback(async () => { try { const { data } = await authApi.get("/customer/orders"); setOrders(data.orders); } catch { setOrders([]); } }, [authApi]);
  const fetchCerts = useCallback(async () => { try { const { data } = await authApi.get("/customer/certificates"); setCerts(data.certificates); } catch { setCerts([]); } }, [authApi]);
  const fetchProgress = useCallback(async () => { try { const { data } = await authApi.get("/customer/progress"); setProgress(data.progress); } catch { setProgress([]); } }, [authApi]);
  const fetchWishlist = useCallback(async () => { try { const { data } = await authApi.get("/customer/wishlist"); setWishlist(data.wishlist); } catch { setWishlist([]); } }, [authApi]);
  const fetchTickets = useCallback(async () => { try { const { data } = await authApi.get("/customer/tickets"); setTickets(data.tickets); } catch { setTickets([]); } }, [authApi]);

  useEffect(() => {
    if (!user) return;
    if (tab === "overview") { fetchOrders(); fetchCerts(); fetchProgress(); }
    if (tab === "orders") fetchOrders();
    if (tab === "certificates") fetchCerts();
    if (tab === "courses") fetchProgress();
    if (tab === "wishlist") fetchWishlist();
    if (tab === "tickets") fetchTickets();
  }, [tab, user, fetchOrders, fetchCerts, fetchProgress, fetchWishlist, fetchTickets]);

  if (loading || !user) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-taven-gold" /></div>;

  const doLogout = () => { logout(); toast.success("Signed out"); navigate("/"); };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-10" data-testid="account-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-taven-gold font-bold">My TAVEN</p>
          <h1 className="text-2xl md:text-3xl font-black font-display">Welcome, {user.first_name || "friend"}</h1>
        </div>
        <button onClick={doLogout} data-testid="account-logout" className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider border border-black/15 px-4 py-2.5 hover:bg-black hover:text-white transition-colors"><LogOut className="w-4 h-4" /> Sign Out</button>
      </div>

      <div className="grid md:grid-cols-[220px_1fr] gap-8">
        <nav className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible pb-2">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`account-tab-${t.key}`}
              className={`flex items-center gap-2.5 px-4 py-3 text-sm font-bold whitespace-nowrap transition-colors ${tab === t.key ? "bg-taven-black text-white" : "hover:bg-taven-gray"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </nav>

        <div className="min-w-0">
          {tab === "overview" && (
            <div className="space-y-6" data-testid="overview-tab">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: "Orders", value: orders?.length ?? "—", icon: Package },
                  { label: "Courses", value: progress?.length ?? "—", icon: GraduationCap },
                  { label: "Certificates", value: certs?.length ?? "—", icon: Award },
                  { label: "Wishlist", value: user.wishlist?.length ?? 0, icon: Heart },
                ].map((s) => (
                  <div key={s.label} className="bg-white border border-black/10 p-5">
                    <s.icon className="w-5 h-5 text-taven-gold" />
                    <p className="text-3xl font-black mt-2">{s.value}</p>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{s.label}</p>
                  </div>
                ))}
              </div>
              <div className="bg-white border border-black/10 p-6">
                <h3 className="font-bold mb-3">Quick actions</h3>
                <div className="flex flex-wrap gap-3">
                  <Link to="/shop" className="text-xs font-bold uppercase tracking-wider bg-taven-gray px-4 py-2.5 hover:bg-taven-gold hover:text-white transition-colors">Continue Shopping</Link>
                  <Link to="/education" className="text-xs font-bold uppercase tracking-wider bg-taven-gray px-4 py-2.5 hover:bg-taven-gold hover:text-white transition-colors">Browse Academy</Link>
                  <Link to="/track" className="text-xs font-bold uppercase tracking-wider bg-taven-gray px-4 py-2.5 hover:bg-taven-gold hover:text-white transition-colors">Track an Order</Link>
                </div>
              </div>
            </div>
          )}

          {tab === "orders" && (
            <div data-testid="orders-tab">
              {orders === null ? <Loader2 className="w-5 h-5 animate-spin text-taven-gold" /> :
                orders.length === 0 ? <Empty icon={Package} text="You have no orders yet." cta="Start shopping →" to="/shop" /> :
                  <div className="space-y-4">
                    {orders.map((o, i) => (
                      <div key={i} className="bg-white border border-black/10 p-5" data-testid="order-card">
                        <div className="flex items-center justify-between flex-wrap gap-2 border-b border-black/5 pb-3">
                          <div><p className="font-bold">{o.name || `#${o.order_number}`}</p><p className="text-xs text-muted-foreground">{o.date ? new Date(o.date).toLocaleDateString() : ""}</p></div>
                          <span className="text-xs font-bold uppercase tracking-wider text-taven-orange flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> {o.status}</span>
                        </div>
                        <div className="mt-3 space-y-2">
                          {(o.items || []).map((it, j) => (
                            <div key={j} className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-taven-gray overflow-hidden shrink-0">{it.image && <img src={it.image} alt={it.title} className="w-full h-full object-cover" />}</div>
                              <span className="text-sm flex-1">{it.title} <span className="text-muted-foreground">× {it.quantity}</span></span>
                            </div>
                          ))}
                        </div>
                        <p className="text-right text-sm font-bold mt-3">{money(o.total, o.currency)}</p>
                      </div>
                    ))}
                  </div>}
            </div>
          )}

          {tab === "courses" && (
            <div data-testid="courses-tab">
              {progress === null ? <Loader2 className="w-5 h-5 animate-spin text-taven-gold" /> :
                progress.length === 0 ? <Empty icon={GraduationCap} text="You haven't started any courses yet." cta="Explore TAVEN Academy →" to="/education" /> :
                  <div className="space-y-3">
                    {progress.map((p, i) => (
                      <Link to={`/education/${p.course_slug}`} key={i} className="block bg-white border border-black/10 p-5 hover:border-taven-gold transition-colors" data-testid="course-progress-card">
                        <div className="flex items-center justify-between"><p className="font-bold">{p.course_title}</p><span className="text-taven-gold font-bold text-sm">{p.percent}%</span></div>
                        <div className="h-2 bg-taven-gray mt-2"><div className="h-2 bg-taven-orange" style={{ width: `${p.percent}%` }} /></div>
                      </Link>
                    ))}
                  </div>}
            </div>
          )}

          {tab === "certificates" && (
            <div data-testid="certificates-tab">
              {certs === null ? <Loader2 className="w-5 h-5 animate-spin text-taven-gold" /> :
                certs.length === 0 ? <Empty icon={Award} text="No certificates yet. Complete a course to earn one." cta="Go to Academy →" to="/education" /> :
                  <div className="grid sm:grid-cols-2 gap-4">
                    {certs.map((c) => (
                      <div key={c.certificate_id} className="bg-white border border-black/10 p-5" data-testid="certificate-card">
                        <Award className="w-8 h-8 text-taven-gold" />
                        <p className="font-bold mt-2">{c.course_title}</p>
                        <p className="text-xs text-muted-foreground mt-1">ID: {c.certificate_id}</p>
                        <p className="text-xs text-muted-foreground">Issued {c.issued_date ? new Date(c.issued_date).toLocaleDateString() : ""}</p>
                        <Link to={`/verify/${c.certificate_id}`} className="inline-block mt-3 text-xs font-bold uppercase tracking-wider text-taven-gold hover:underline">View / Verify →</Link>
                      </div>
                    ))}
                  </div>}
            </div>
          )}

          {tab === "wishlist" && (
            <div data-testid="wishlist-tab">
              {wishlist === null ? <Loader2 className="w-5 h-5 animate-spin text-taven-gold" /> :
                wishlist.length === 0 ? <Empty icon={Heart} text="Your wishlist is empty." cta="Find something you love →" to="/shop" /> :
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {wishlist.map((w) => (
                      <div key={w.handle} className="bg-white border border-black/10 group" data-testid="wishlist-card">
                        <Link to={`/product/${w.handle}`}><div className="aspect-square bg-taven-gray overflow-hidden">{w.image && <img src={w.image} alt={w.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />}</div></Link>
                        <div className="p-3">
                          <p className="text-sm font-semibold line-clamp-1">{w.title}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-sm font-bold">{money(w.price)}</span>
                            <button onClick={async () => { await authApi.delete(`/customer/wishlist/${w.handle}`); setWishlist(wishlist.filter((x) => x.handle !== w.handle)); refreshUser(); }} data-testid="wishlist-remove" className="text-xs text-taven-red hover:underline">Remove</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>}
            </div>
          )}

          {tab === "tickets" && <TicketsPanel authApi={authApi} tickets={tickets} reload={fetchTickets} />}

          {tab === "profile" && <ProfilePanel user={user} authApi={authApi} refreshUser={refreshUser} />}
        </div>
      </div>
    </div>
  );
}

const TicketsPanel = ({ authApi, tickets, reload }) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ subject: "", category: "general", message: "", order_number: "" });
  const [reply, setReply] = useState("");

  const create = async (e) => {
    e.preventDefault();
    try { await authApi.post("/tickets", form); toast.success("Ticket created"); setOpen(false); setForm({ subject: "", category: "general", message: "", order_number: "" }); reload(); }
    catch { toast.error("Could not create ticket"); }
  };
  const openTicket = async (tid) => { try { const { data } = await authApi.get(`/customer/tickets/${tid}`); setSelected(data.ticket); } catch { toast.error("Could not load ticket"); } };
  const sendReply = async () => {
    if (!reply.trim()) return;
    try { await authApi.post(`/customer/tickets/${selected.ticket_id}/reply`, { message: reply }); setReply(""); openTicket(selected.ticket_id); reload(); }
    catch { toast.error("Could not send reply"); }
  };

  if (selected) return (
    <div data-testid="ticket-detail">
      <button onClick={() => setSelected(null)} className="text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-black mb-4">← Back to tickets</button>
      <div className="bg-white border border-black/10 p-5">
        <div className="flex items-center justify-between border-b border-black/10 pb-3">
          <div><p className="font-bold">{selected.subject}</p><p className="text-xs text-muted-foreground">{selected.ticket_id} · {selected.status}</p></div>
        </div>
        <div className="space-y-3 py-4">
          {selected.messages.map((m, i) => (
            <div key={i} className={`max-w-[85%] px-3.5 py-2.5 text-sm ${m.from === "customer" ? "bg-taven-gray ml-auto" : "bg-taven-black text-white"}`}>
              <p className="text-[10px] uppercase tracking-wider font-bold opacity-70 mb-1">{m.author}</p>
              {m.body}
            </div>
          ))}
        </div>
        <div className="flex gap-2 border-t border-black/10 pt-3">
          <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type your reply…" data-testid="ticket-reply-input" className="flex-1 border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-taven-gold" />
          <button onClick={sendReply} data-testid="ticket-reply-send" className="bg-taven-orange text-white p-2.5 hover:bg-[#d9561a]"><Send className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="tickets-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold">Support Tickets</h3>
        <button onClick={() => setOpen(!open)} data-testid="new-ticket-btn" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-taven-black text-white px-4 py-2.5 hover:bg-taven-orange transition-colors"><Plus className="w-4 h-4" /> New Ticket</button>
      </div>
      {open && (
        <form onSubmit={create} className="bg-white border border-black/10 p-5 mb-4 space-y-3" data-testid="ticket-form">
          <input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Subject" data-testid="ticket-subject" className="w-full border border-black/15 px-4 py-2.5 text-sm outline-none focus:border-taven-gold" />
          <div className="grid sm:grid-cols-2 gap-3">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="ticket-category" className="border border-black/15 px-4 py-2.5 text-sm outline-none">
              {["general", "orders", "shipping", "returns", "courses", "certificates", "affiliate", "seller"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input value={form.order_number} onChange={(e) => setForm({ ...form, order_number: e.target.value })} placeholder="Order # (optional)" data-testid="ticket-order" className="border border-black/15 px-4 py-2.5 text-sm outline-none focus:border-taven-gold" />
          </div>
          <textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} placeholder="How can we help?" data-testid="ticket-message" className="w-full border border-black/15 px-4 py-2.5 text-sm outline-none focus:border-taven-gold" />
          <button data-testid="ticket-submit" className="bg-taven-black text-white font-bold uppercase tracking-wider text-xs py-3 px-6 hover:bg-taven-orange transition-colors">Submit Ticket</button>
        </form>
      )}
      {tickets === null ? <Loader2 className="w-5 h-5 animate-spin text-taven-gold" /> :
        tickets.length === 0 ? <Empty icon={MessageSquare} text="No support tickets yet." /> :
          <div className="space-y-3">
            {tickets.map((t) => (
              <button key={t.ticket_id} onClick={() => openTicket(t.ticket_id)} data-testid="ticket-row" className="w-full text-left bg-white border border-black/10 p-4 hover:border-taven-gold transition-colors">
                <div className="flex items-center justify-between"><p className="font-bold text-sm">{t.subject}</p><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 ${t.status === "answered" ? "bg-green-50 text-green-700" : "bg-taven-gray"}`}>{t.status}</span></div>
                <p className="text-xs text-muted-foreground mt-1">{t.ticket_id} · {t.category} · {new Date(t.updated_at).toLocaleDateString()}</p>
              </button>
            ))}
          </div>}
    </div>
  );
};

const ProfilePanel = ({ user, authApi, refreshUser }) => {
  const [form, setForm] = useState({ first_name: user.first_name || "", last_name: user.last_name || "", phone: user.phone || "", marketing_consent: user.marketing_consent });
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await authApi.put("/customer/me", form); await refreshUser(); toast.success("Profile updated"); }
    catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={save} className="bg-white border border-black/10 p-6 max-w-lg space-y-4" data-testid="profile-tab">
      <div className="grid grid-cols-2 gap-3">
        <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="First name" data-testid="profile-first" className="border border-black/15 px-4 py-3 text-sm outline-none focus:border-taven-gold" />
        <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Last name" data-testid="profile-last" className="border border-black/15 px-4 py-3 text-sm outline-none focus:border-taven-gold" />
      </div>
      <input value={user.email} disabled className="w-full border border-black/10 bg-taven-gray/40 px-4 py-3 text-sm text-muted-foreground" />
      <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" data-testid="profile-phone" className="w-full border border-black/15 px-4 py-3 text-sm outline-none focus:border-taven-gold" />
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.marketing_consent} onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })} data-testid="profile-consent" /> Receive TAVEN updates & offers</label>
      <button disabled={saving} data-testid="profile-save" className="bg-taven-black text-white font-bold uppercase tracking-wider text-xs py-3 px-6 hover:bg-taven-orange transition-colors disabled:opacity-60">{saving ? "Saving…" : "Save Changes"}</button>
    </form>
  );
};
